// Service types for the directory
export type ServiceType =
  | 'hvac'
  | 'plumbing'
  | 'electrical'
  | 'heating'
  | 'cooling'
  | 'ventilation'
  | 'refrigeration'
  | 'water_heater'
  | 'duct_cleaning'
  | 'insulation';

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  heating: 'Heating',
  cooling: 'Cooling',
  ventilation: 'Ventilation',
  refrigeration: 'Refrigeration',
  water_heater: 'Water Heater',
  duct_cleaning: 'Duct Cleaning',
  insulation: 'Insulation',
};

export const SERVICE_TYPE_COLORS: Record<ServiceType, string> = {
  hvac: 'bg-blue-100 text-blue-800',
  plumbing: 'bg-cyan-100 text-cyan-800',
  electrical: 'bg-yellow-100 text-yellow-800',
  heating: 'bg-orange-100 text-orange-800',
  cooling: 'bg-sky-100 text-sky-800',
  ventilation: 'bg-teal-100 text-teal-800',
  refrigeration: 'bg-indigo-100 text-indigo-800',
  water_heater: 'bg-red-100 text-red-800',
  duct_cleaning: 'bg-gray-100 text-gray-800',
  insulation: 'bg-green-100 text-green-800',
};

// Business hours type
export interface DayHours {
  open: string;
  close: string;
}

export type BusinessHours = {
  [key in 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday']?: DayHours | null;
};

// Service Provider
export interface ServiceProvider {
  id: string;
  userId: string | null;
  organizationId: string | null;
  businessName: string;
  slug: string;
  description: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  serviceTypes: ServiceType[];
  specialties: string[];
  serviceAreaMiles: number;
  isVerified: boolean;
  isClaimed: boolean;
  logoUrl: string | null;
  coverImageUrl: string | null;
  galleryUrls: string[];
  hours: BusinessHours;
  certifications: string[];
  yearsInBusiness: number | null;
  averageRating: number | null;
  reviewCount: number;
  createdAt: Date;
  updatedAt: Date;
}

// Provider Review
export interface ProviderReview {
  id: string;
  providerId: string;
  userId: string;
  userName: string | null;
  rating: number;
  title: string | null;
  content: string | null;
  serviceType: ServiceType | null;
  verifiedCustomer: boolean;
  response: string | null;
  respondedAt: Date | null;
  createdAt: Date;
}

// Provider Photo
export interface ProviderPhoto {
  id: string;
  providerId: string;
  url: string;
  caption: string | null;
  isCover: boolean;
  order: number;
  createdAt: Date;
}

// Search/filter types
export interface SearchFilters {
  query?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  serviceTypes?: ServiceType[];
  minRating?: number;
  maxDistance?: number;
  isVerified?: boolean;
}

export interface SearchResult {
  provider: ServiceProvider;
  distance?: number;
}

// Map bounds
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

// Analytics types for provider dashboard
export interface ProviderAnalytics {
  totalViews: number;
  totalClicks: number;
  viewsThisMonth: number;
  clicksThisMonth: number;
  viewsByDay: { date: string; views: number }[];
  clicksByDay: { date: string; clicks: number }[];
}
